import { useSettingsContext } from "../../../hook/useSettingsContext";
import * as Slider from "@radix-ui/react-slider";
import * as Tooltip from "@radix-ui/react-tooltip";
import QuantityInput from "./QuantityInput";

export default function QuantitySlider() {
    const { previewOptions, quantity, setQuantity, getOption } =
        useSettingsContext();

    const showInput = getOption("showInput", "slider");
    const thumbQuantity = getOption("thumbQuantity", "slider");
    const inputPosition = getOption("inputPosition", "slider");
    const showTooltip = getOption("showTooltip", "slider");
    const tooltipFormat = getOption("tooltipFormat", "slider");

    // Handle slider value change - Radix Slider passes an array
    const handleSliderChange = (newValue) => {
        const value = newValue[0]; // Extract the first value from the array

        // Apply the same validation logic
        if (
            value >= previewOptions.minValue &&
            (previewOptions.maxValue === null ||
                value <= previewOptions.maxValue)
        ) {
            setQuantity(value);
        }
    };

    // Display value based on fractional setting
    const displayValue =
        previewOptions.step < 1 ? quantity.toFixed(1) : Math.round(quantity);

    const formatTooltip = (template, values) => {
        if (!template) return false;
        return template.replace(/\{(\w+)\}/g, (match, key) => {
            return values[key] !== undefined ? values[key] : match;
        });
    };

    const tooltipContent = formatTooltip(tooltipFormat, {
        step: previewOptions.step,
        min: previewOptions.minValue,
        max: previewOptions.maxValue || "",
    });

    return (
        <Tooltip.Provider delayDuration={0}>
            <div
                className={`wpb-pmqb-quantity-slider flex items-center gap-[var(--wpb-quantity-slider-gap)] ${
                    inputPosition === "top" || inputPosition === "bottom"
                        ? "flex-col"
                        : ""
                }`}
            >
                {showInput &&
                    (inputPosition === "top" || inputPosition === "left") && (
                        <QuantityInput
                            className="w-auto h-4 min-h-0 py-2 border-0 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:appearance-none"
                            style={{
                                color: "var(--wpb-quantity-slider-input-color)",
                                fontSize:
                                    "var(--wpb-quantity-slider-input-font-size)",
                                fontWeight:
                                    "var(--wpb-quantity-slider-input-font-weight)",
                            }}
                        />
                    )}
                <div className="flex items-center gap-3">
                    <Slider.Root
                        className="relative flex items-center select-none touch-none min-w-[160px] h-8"
                        value={[quantity]}
                        onValueChange={handleSliderChange}
                        min={previewOptions.minValue}
                        max={previewOptions.maxValue || 100}
                        step={previewOptions.step}
                    >
                        <Slider.Track className="bg-[var(--wpb-quantity-slider-track-color)] relative grow rounded-full h-[var(--wpb-quantity-slider-height)]">
                            <Slider.Range className="absolute bg-[var(--wpb-quantity-slider-thumb-bg)] rounded-full h-full" />
                        </Slider.Track>
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <Slider.Thumb
                                    className="flex items-center justify-center w-[var(--wpb-quantity-slider-thumb-size)] h-[var(--wpb-quantity-slider-thumb-size)] bg-[var(--wpb-quantity-slider-thumb-bg)] rounded-full shadow-md hover:shadow-lg focus:outline-none focus:scale-125 transition-all cursor-grab active:cursor-grabbing"
                                    style={{
                                        color: "var(--wpb-quantity-slider-thumb-color)",
                                        fontSize:
                                            "var(--wpb-quantity-slider-thumb-font-size)",
                                        fontWeight:
                                            "var(--wpb-quantity-slider-thumb-font-weight)",
                                    }}
                                    aria-label="Quantity"
                                >
                                    {thumbQuantity && displayValue}
                                </Slider.Thumb>
                            </Tooltip.Trigger>
                            {tooltipContent && showTooltip && (
                                <Tooltip.Portal
                                    container={document.querySelector(
                                        ".wpb-pmqb-app-wrapper",
                                    )}
                                >
                                    <Tooltip.Content
                                        className="bg-gray-900 text-white px-3 py-1.5 rounded text-xs font-medium shadow-lg"
                                        sideOffset={8}
                                        style={{ zIndex: 999999 }}
                                    >
                                        {tooltipContent}
                                        <Tooltip.Arrow className="fill-gray-900" />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            )}
                        </Tooltip.Root>
                    </Slider.Root>
                </div>
                {showInput &&
                    (inputPosition === "bottom" ||
                        inputPosition === "right") && (
                        <QuantityInput
                            className="w-auto h-4 min-h-0 py-2 border-0 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:appearance-none"
                            style={{
                                color: "var(--wpb-quantity-slider-input-color)",
                                fontSize:
                                    "var(--wpb-quantity-slider-input-font-size)",
                                fontWeight:
                                    "var(--wpb-quantity-slider-input-font-weight)",
                            }}
                        />
                    )}
            </div>
        </Tooltip.Provider>
    );
}
